import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import {
	Floor,
	RealEstate,
	RealEstateService,
	ServiceCompany,
	ServiceCompanyDevice,
	ServiceCompanyService,
	TenantUnitDetail,
	TenantUnitService,
} from '@eadsysteme/ewsrealestateclient-v2';
import { SessionService } from '../../services/session.service';
import { LayoutService } from '../../../@vex/services/layout.service';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ActivatedRoute, Router, RouterLink, UrlSegment } from '@angular/router';
import { Billing, BillingService } from '@eadsysteme/billingclient-v1';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { zip } from 'rxjs';
import { Contact, ContactService } from '@eadsysteme/contactclient-v1';
import { fadeInUp400ms } from '../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../@vex/animations/stagger.animation';
import { MatCard, MatCardContent, MatCardSubtitle } from '@angular/material/card';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { RecentRealEstateCacheService } from '../../../@vex/services/recent-real-estate-cache.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { PopoverService } from '../../../@vex/components/popover/popover.service';
import { PopoverRef } from '../../../@vex/components/popover/popover-ref';
import { TenantUnitSelectPanelComponent, TenantUnitSelectPanelData } from './tenant-unit-select-panel/tenant-unit-select-panel.component';
import FloorTypeEnum = Floor.FloorTypeEnum;

@Component({
	selector: 'ara-tenant-detail-cards',
	templateUrl: './tenant-detail-cards.component.html',
	styleUrls: ['./tenant-detail-cards.component.css'],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [NgTemplateOutlet, TranslocoDirective, RouterLink, MatCard, MatCardSubtitle, MatCardContent, DatePipe, MatProgressSpinner, MatIcon, MatTooltip],
	changeDetection: ChangeDetectionStrategy.Eager,
	animations: [fadeInUp400ms, stagger40ms],
})
export class TenantDetailCardsComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);
	private readonly router = inject(Router);
	private readonly recentCache = inject(RecentRealEstateCacheService);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly billingService = inject(BillingService);
	private readonly servicecompanyService = inject(ServiceCompanyService);
	private readonly realEstateService = inject(RealEstateService);
	private readonly contactService = inject(ContactService);
	private readonly tenantUnitService = inject(TenantUnitService);
	private readonly translocoService = inject(TranslocoService);
	private readonly popoverService = inject(PopoverService);
	protected readonly sessionService = inject(SessionService);
	protected readonly layoutService = inject(LayoutService);

	private unitPanelRef: PopoverRef<TenantUnitSelectPanelData> | null = null;
	private unitPanelCloseTimer: ReturnType<typeof setTimeout> | null = null;

	selectedServiceCompany = signal<ServiceCompany>(null);
	selectedRealEstate = signal<RealEstate>(null);
	selectedPropertyManagement = signal<Contact>(null);
	selectedTenantUnit = signal<TenantUnitDetail>(null);
	selectedDevice = signal<ServiceCompanyDevice>(null);
	readonly tenantUnits = signal<TenantUnitDetail[] | null>(null);
	billingsPathSelected = signal<boolean>(false);
	selectedBilling = signal<Billing>({ billingNumber: 0, realEstateId: null });

	currentRealEstateId: string;
	currentRealEstateArea: number;

	ngOnInit(): void {
		this.activatedRoute.url.subscribe({
			next: (value) => {
				// Re-resolve the area on every URL change so stale sessionStorage never wins.
				// Priority: localStorage cache (all areas) → sessionStorage fallback
				const urlRealEstateId = this.activatedRoute.snapshot.paramMap.get('realEstateId');
				const sessionArea = Number(sessionStorage.getItem('selectedRealEstateArea')) || 0;
				const sessionId = sessionStorage.getItem('selectedRealEstateID');

				const effectiveId = urlRealEstateId ?? sessionId;
				const cachedArea = effectiveId ? RecentRealEstateCacheService.findAreaByRealEstateId(effectiveId) : null;

				this.currentRealEstateArea = cachedArea ?? sessionArea;
				this.currentRealEstateId = effectiveId ?? sessionId;

				this.selectedServiceCompany.set(undefined);
				this.selectedPropertyManagement.set(undefined);
				this.selectedRealEstate.set(undefined);
				this.selectedTenantUnit.set(undefined);
				this.selectedDevice.set(undefined);
				this.tenantUnits.set(null);

				this.loadRealEstateContactFromUrl(value);
				this.loadTenantUnitFromUrl(value);
				this.loadDeviceFromUrl(value);
				this.loadBillingsFromUrl(value);
			},
		});
	}

	private loadDeviceFromUrl(value: UrlSegment[]) {
		if (
			value?.length >= 3 &&
			(value[2]?.path === 'devices' || (value[0]?.path === 'main' && (value[3]?.path === 'devices' || value[5]?.path === 'devices')))
		) {
			const currentDeviceId = this.activatedRoute.snapshot.paramMap.get('deviceId');
			this.loadDevice(currentDeviceId);
		}
	}

	private loadTenantUnitFromUrl(value: UrlSegment[]) {
		if (
			value?.length >= 2 &&
			(value[2]?.path === 'tenantunits' ||
				((value[0]?.path === 'main' || value[0]?.path === 'ci' || value[0]?.path === 'smoke-detector-checks') && value[3]?.path === 'tenantunits'))
		) {
			const currentRealEstateId = this.activatedRoute.snapshot.paramMap.get('realEstateId');
			const currentTenantUnitId = this.activatedRoute.snapshot.paramMap.get('tenantUnitId');
			this.loadTenantUnit(currentRealEstateId, currentTenantUnitId);
		}
	}

	private loadRealEstateContactFromUrl(value: UrlSegment[]) {
		if (
			value[0]?.path === 'realestates' ||
			value[0]?.path === 'smoke-detector-checks' ||
			((value[0]?.path === 'main' || value[0]?.path === 'ci') && value[1]?.path === 'realestates')
		) {
			const currentRealEstateId = this.currentRealEstateId || this.activatedRoute.snapshot.paramMap.get('realEstateId');
			zip(
				this.realEstateService.getRealEstate(this.currentRealEstateArea, currentRealEstateId),
				this.servicecompanyService.getServiceCompany(this.currentRealEstateArea)
			)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe({
					next: (response) => {
						this.selectedRealEstate.set(response[0]);
						this.selectedServiceCompany.set(response[1]);
						this.loadContact();
						if (response[0]?.id && response[0]?.area != null && response[0]?.number != null) {
							this.recentCache.addRecent({
								id: response[0].id,
								area: response[0].area,
								number: response[0].number,
								address: RecentRealEstateCacheService.toAddressTableEntity(response[0].address),
							});
						}
					},
				});
		}
	}

	private loadBillingsFromUrl(value: UrlSegment[]) {
		if (value?.length >= 2 && value[0]?.path === 'billing-units') {
			this.billingsPathSelected.set(true);
			const routeParams = this.activatedRoute.snapshot.paramMap;
			const billingId = routeParams.get('billingId');
			this.billingService.readBilling(this.currentRealEstateArea, billingId).subscribe({
				next: (billing) => {
					this.selectedBilling.set(billing);
					this.loadRealEstateForBilling(billing);
				},
			});
		} else {
			this.billingsPathSelected.set(false);
		}
	}

	private loadRealEstateForBilling(billing: Billing) {
		const area = RecentRealEstateCacheService.findAreaByRealEstateId(billing.realEstateId) ?? this.currentRealEstateArea;
		this.currentRealEstateArea = area;
		zip(this.realEstateService.getRealEstate(area, billing.realEstateId), this.servicecompanyService.getServiceCompany(area))
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (response) => {
					this.selectedRealEstate.set(response[0]);
					this.selectedServiceCompany.set(response[1]);
					this.loadContact();
					if (response[0]?.id && response[0]?.area != null && response[0]?.number != null) {
						this.recentCache.addRecent({
							id: response[0].id,
							area: response[0].area,
							number: response[0].number,
							address: RecentRealEstateCacheService.toAddressTableEntity(response[0].address),
						});
					}
				},
			});
	}

	private loadContact() {
		const contactId = this.selectedRealEstate()?.propertyManagementContactId;
		if (!contactId) {
			this.selectedPropertyManagement.set(null);
			return;
		}
		this.contactService
			.getContactById(this.currentRealEstateArea ?? this.sessionService.effectiveArea, contactId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (contact) => {
					this.selectedPropertyManagement.set(contact);
				},
				error: () => {
					this.selectedPropertyManagement.set(null);
				},
			});
	}

	private loadDevice(currentDeviceId: string) {
		this.servicecompanyService
			.getServiceCompanyDevice(this.currentRealEstateArea, currentDeviceId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (device) => {
					this.selectedDevice.set(device);
				},
			});
	}

	private loadTenantUnit(currentRealEstateId: string, currentTenantUnitId: string) {
		// Load selected unit first → card appears immediately
		this.tenantUnitService
			.getTenantUnitById(this.currentRealEstateArea, currentRealEstateId, currentTenantUnitId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (tenantUnit) => {
					this.selectedTenantUnit.set(tenantUnit);
				},
			});
		// Load all units in parallel → nav buttons appear once ready (null = loading)
		this.tenantUnitService
			.getByRealEstateId(this.currentRealEstateArea, currentRealEstateId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (allUnits) => {
					this.tenantUnits.set(allUnits ?? []);
				},
			});
	}

	/* ── Tenant-Unit Navigation ──────────────────────────────────────────── */

	onNavigatorAction(action: 'first' | 'prev' | 'next' | 'last'): void {
		const units = this.tenantUnits();
		if (!units?.length) return;

		const currentIndex = this.getSelectedTenantUnitIndex();
		let targetIndex = currentIndex;

		switch (action) {
			case 'first':
				targetIndex = 0;
				break;
			case 'prev':
				targetIndex = currentIndex > 0 ? currentIndex - 1 : 0;
				break;
			case 'next':
				targetIndex = currentIndex >= 0 ? Math.min(currentIndex + 1, units.length - 1) : 0;
				break;
			case 'last':
				targetIndex = units.length - 1;
				break;
		}

		const targetId = units[targetIndex]?.id;
		if (targetId && targetId !== this.selectedTenantUnit()?.id) {
			const realEstateId = this.selectedRealEstate()?.id;
			this.router
				.navigateByUrl('/', { skipLocationChange: true })
				.then(() => this.router.navigate(['/realestates', realEstateId, 'tenantunits', targetId]));
		}
	}

	canNavigatePrev(): boolean {
		return this.getSelectedTenantUnitIndex() > 0;
	}

	canNavigateNext(): boolean {
		const index = this.getSelectedTenantUnitIndex();
		return index >= 0 && index < (this.tenantUnits()?.length ?? 0) - 1;
	}

	private getSelectedTenantUnitIndex(): number {
		const currentId = this.selectedTenantUnit()?.id;
		if (!currentId) return -1;
		return (this.tenantUnits() ?? []).findIndex((unit) => unit.id === currentId);
	}

	private getTenantUnitLabel(tenantUnit?: TenantUnitDetail): string {
		if (!tenantUnit) return '';
		const number = tenantUnit.number ?? '';
		const subNumber = tenantUnit.subNumber ?? '';
		return subNumber === '' ? `${number}` : `${number} / ${subNumber}`;
	}

	getFirstTenantUnitLabel(): string {
		return this.getTenantUnitLabel((this.tenantUnits() ?? [])[0]);
	}

	getPrevTenantUnitLabel(): string {
		const index = this.getSelectedTenantUnitIndex();
		return this.getTenantUnitLabel(index > 0 ? (this.tenantUnits() ?? [])[index - 1] : undefined);
	}

	getNextTenantUnitLabel(): string {
		const index = this.getSelectedTenantUnitIndex();
		return this.getTenantUnitLabel(index >= 0 ? (this.tenantUnits() ?? [])[index + 1] : undefined);
	}

	getLastTenantUnitLabel(): string {
		const units = this.tenantUnits() ?? [];
		return this.getTenantUnitLabel(units.length ? units.at(-1) : undefined);
	}

	openTenantUnitPanel(origin: HTMLElement): void {
		this.cancelUnitPanelClose();
		if (this.unitPanelRef) return;

		const units = this.tenantUnits();
		if (!units?.length) return;

		this.unitPanelRef = this.popoverService.open<TenantUnitSelectPanelData>({
			content: TenantUnitSelectPanelComponent,
			origin,
			hasBackdrop: false,
			data: {
				units,
				activeId: this.selectedTenantUnit()?.id,
				onSelect: (unit: TenantUnitDetail) => {
					this.closeUnitPanel();
					if (unit.id !== this.selectedTenantUnit()?.id) {
						const realEstateId = this.selectedRealEstate()?.id;
						this.router
							.navigateByUrl('/', { skipLocationChange: true })
							.then(() => this.router.navigate(['/realestates', realEstateId, 'tenantunits', unit.id]));
					}
				},
				onPanelEnter: () => this.cancelUnitPanelClose(),
				onPanelLeave: () => this.scheduleUnitPanelClose(),
			},
			width: 360,
			position: [
				{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' },
				{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom' },
			],
			offsetY: 4,
		});
		this.unitPanelRef.afterClosed$.subscribe(() => {
			this.unitPanelRef = null;
		});
	}

	onUnitBtnLeave(): void {
		this.scheduleUnitPanelClose();
	}

	private scheduleUnitPanelClose(): void {
		this.cancelUnitPanelClose();
		this.unitPanelCloseTimer = setTimeout(() => this.closeUnitPanel(), 300);
	}

	private cancelUnitPanelClose(): void {
		if (this.unitPanelCloseTimer) {
			clearTimeout(this.unitPanelCloseTimer);
			this.unitPanelCloseTimer = null;
		}
	}

	private closeUnitPanel(): void {
		this.cancelUnitPanelClose();
		this.unitPanelRef?.close();
		this.unitPanelRef = null;
	}

	getIconUrl(area: number): string {
		const base64 = this.sessionService.cachedFavIconsByArea.get(area);
		return base64 ? `data:image/png;base64,${base64}` : '';
	}

	beautifyFloor(): string {
		let result = '';
		if (this.selectedTenantUnit()?.floorType === FloorTypeEnum.GROUND_FLOOR || this.selectedTenantUnit()?.floorType === FloorTypeEnum.ATTIC) {
			result = [this.translocoService.translate('floorType.' + this.selectedTenantUnit().floorType), this.selectedTenantUnit().location]?.join(' ');
		} else {
			result = [
				Math.abs(this.selectedTenantUnit()?.floorNumber) + '.',
				this.translocoService.translate('floorType.' + this.selectedTenantUnit()?.floorType),
				this.selectedTenantUnit()?.location,
			]?.join(' ');
		}
		return result;
	}
}
